import { useEffect, useRef, useState } from "react";
import { loadPdfEngine } from "../lib/pdfEngine";

type PdfCanvasProps = {
  file: File;
  pageNumber: number;
};

export function PdfCanvas({ file, pageNumber }: PdfCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};

    async function render() {
      const canvas = canvasRef.current;
      const host = hostRef.current;
      if (!canvas || !host) return;

      try {
        setError("");
        const { getDocument } = await loadPdfEngine();
        const data = new Uint8Array(await file.arrayBuffer());
        const loadingTask = getDocument({ data });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });
        const availableWidth = Math.min(host.clientWidth - 24, 720);
        const scale = Math.max(0.7, availableWidth / baseViewport.width);
        const viewport = page.getViewport({ scale });
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        const context = canvas.getContext("2d");
        if (!context || cancelled) return;

        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);

        const task = page.render({ canvasContext: context, viewport, canvas });
        await task.promise;
        cleanup = () => {
          page.cleanup();
          void loadingTask.destroy();
        };
      } catch {
        if (!cancelled) setError("PDF önizlemesi oluşturulamadı.");
      }
    }

    void render();
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [file, pageNumber]);

  return (
    <div className="pdf-canvas-host" ref={hostRef}>
      {error ? <p className="form-error">{error}</p> : <canvas ref={canvasRef} />}
    </div>
  );
}
